import React, { Component } from "react";
import PropTypes from "prop-types";
import { gql, useQuery } from "@apollo/client";
import { withTheme } from "@material-ui/core";
import { darkMapStyles } from "@zeliot/common/constants/mapStyles";
import "./Map.css";
import TrafficControl from "./TrafficControl";
import { getCookie } from "../../../../../../../helpers/localStorage";
import { FiSearch, FiSettings, FiX } from "react-icons/fi";
import SidebarIcons from "./SidebarIcons";

// ===============================
// 🔹 Requête GraphQL
// ===============================
const GET_CLIENT_DETAIL = gql`
  query ($loginId: Int!) {
    clientDetail(loginId: $loginId) {
      lat
      long
    }
  }
`;

class CustomInfoBubble extends google.maps.OverlayView {
  constructor(position, content, map) {
    super();
    this.position = position;
    this.content = content;
    this.map = map;
    this.div = null;
    this.setMap(map);
  }

  onAdd() {
    this.div = document.createElement("div");
    this.div.style.position = "absolute";
    this.div.style.background = "rgba(0,0,0,0.8)";
    this.div.style.color = "white";
    this.div.style.padding = "6px 10px";
    this.div.style.borderRadius = "8px";
    this.div.style.fontSize = "14px";
    this.div.style.whiteSpace = "nowrap";
    this.div.style.cursor = "default";
    this.div.style.boxShadow = "0 2px 6px rgba(0,0,0,0.3)";

    // Contenu
    this.div.innerHTML = `
      ${this.content}
      <span style="
        margin-left:8px;
        font-weight:bold;
        cursor:pointer;
      " class="close-btn">&#10005;</span>
    `;

    // Gestion du clic sur la croix
    this.div.querySelector(".close-btn").addEventListener("click", () => {
      this.setMap(null);
    });

    const panes = this.getPanes();
    panes.overlayMouseTarget.appendChild(this.div);
  }

  draw() {
    const projection = this.getProjection();
    const pos = projection.fromLatLngToDivPixel(this.position);

    if (this.div) {
      this.div.style.left = pos.x + "px";
      this.div.style.top = pos.y - 40 + "px"; // décale la bulle au-dessus du marker
    }
  }

  onRemove() {
    if (this.div) {
      this.div.parentNode.removeChild(this.div);
      this.div = null;
    }
  }
}

// ===============================
// 🔹 Composant principal
// ===============================
class Map extends Component {
  constructor(props) {
    super(props);
    this.mapRef = React.createRef();
    this.overlays = [];
    this.state = {
      drawingEnabled: false,
      coords: [],
      showOverlayBar: false,
      mapInitialized: false,
    };
  }

  map = null;
  drawingManager = null;
  currentOverlay = null;
  markers = [];
  infoWindows = [];
  externalMapTypeListener = null;

  // ===============================
  // 🔸 Initialiser la carte
  // ===============================
  _setMap() {
    const { google } = this.props;
    if (!google) {
      console.error("❌ Google Maps API non disponible");
      return;
    }

    console.log("🗺️ Initialisation de la carte...");

    this.map = new google.maps.Map(this.mapRef.current, {
      center: this.props.center,
      zoom: this.props.zoom,
      clickableIcons: false,
      disableDefaultUI: true,
      zoomControl: true,
      fullscreenControl: true,
      scaleControl: true,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      mapTypeControl: true,
      minZoom: 5,
      maxZoom: 22,
      mapTypeControlOptions: {
        position: google.maps.ControlPosition.TOP_CENTER,
        style: google.maps.MapTypeControlStyle.DROPDOWN_MENU,
        mapTypeIds: [
          google.maps.MapTypeId.ROADMAP,
          google.maps.MapTypeId.SATELLITE,
          google.maps.MapTypeId.HYBRID,
          google.maps.MapTypeId.TERRAIN,
        ],
      },
    });

    // Ajouter un listener pour savoir quand la carte est prête
    google.maps.event.addListenerOnce(this.map, "idle", () => {
      console.log("✅ Carte Google Maps initialisée");
      this.setState({ mapInitialized: true });

        // 🔥 AJOUT ICI
        if (this.props.aois && this.props.aois.length > 0) {
          console.log("🗺️ Dessin des AOIs après init carte");
          this._drawAllAois(this.props.aois);
        }

      // Créer les markers après que la carte soit prête
      if (this.props.markers && this.props.markers.length > 0) {
        console.log("🔄 Création des markers après initialisation carte");
        this._createMarkersFromProps();
      }
    });

    this.props.setMap(this.map);
    if (this.props.setDrawAllAois) {
      this.props.setDrawAllAois(this._drawAllAois.bind(this));
    }

    // Contrôle du trafic
    const trafficLayerControlDiv = document.createElement("div");
    new TrafficControl(google, trafficLayerControlDiv, this.map);
    this.map.controls[google.maps.ControlPosition.RIGHT_TOP].push(
      trafficLayerControlDiv
    );

    // Si des coordonnées initiales sont fournies, afficher le polygone
    if (this.props.initialCoords && this.props.initialCoords.length > 0) {
      this._drawInitialPolygon(this.props.initialCoords);
    }

    this._applyTheme();
    this._initDrawingManager();
  }

  _clearAllOverlays = () => {
    this.overlays.forEach((o) => o.setMap(null));
    this.overlays = [];
  };

  // ===============================
  // 🔸 Dessiner un polygone existant
  // ===============================
  _drawInitialShape(shape) {
    console.log("🟢 _drawInitialShape appelé avec :", shape);
    if (!this.map) {
      console.warn("Map pas encore initialisée, on attend un peu...");
      setTimeout(() => this._drawInitialShape(shape), 500);
      return;
    }

    const { google } = this.props;
    if (!google || !this.map || !shape) return;

    // 🔹 1️⃣ Effacer tout ce qui existe
    this.overlays.forEach((o) => o.setMap(null));
    this.overlays = [];
    this.clearMarkers();

    // Supprimer l'ancien overlay
    if (this.currentOverlay) {
      this.currentOverlay.setMap(null);
    }

    const bounds = new google.maps.LatLngBounds();

    if (shape.type === "polygon") {
      const polygon = new google.maps.Polygon({
        paths: shape.paths,
        editable: true,
        draggable: false,
        strokeColor: "#FF0000",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#FF0000",
        fillOpacity: 0.35,
        map: this.map,
      });

      this.currentOverlay = polygon;
      this._attachPolygonListeners(polygon);

      // Calculer les bounds pour centrer la carte
      shape.paths.forEach((c) => bounds.extend(c));
      this.map.fitBounds(bounds);

      // Ajouter le marker au point fourni dans props.area
      if (
        this.props.area &&
        this.props.area.latitude &&
        this.props.area.longitude
      ) {
        const markerPosition = {
          lat: parseFloat(this.props.area.latitude),
          lng: parseFloat(this.props.area.longitude),
        };

        const marker = new google.maps.Marker({
          position: markerPosition,
          map: this.map,
          animation: google.maps.Animation.DROP,
        });
        this.markers.push(marker);
      }
    } else if (shape.type === "circle") {
      // Marker au centre du cercle
      const marker = new google.maps.Marker({
        position: shape.center,
        map: this.map,
        animation: google.maps.Animation.DROP,
      });
      this.markers.push(marker);

      // Cercle
      const circle = new google.maps.Circle({
        strokeColor: "#000000",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#0000FF",
        fillOpacity: 0.35,
        map: this.map,
        center: shape.center,
        radius: parseFloat(shape.radius),
        editable: true,
        draggable: true,
      });

      this.currentOverlay = circle;

      // Calculer et ajuster les bounds du cercle
      const googlePosition = new google.maps.LatLng(
        shape.center.lat,
        shape.center.lng
      );
      for (let angle = -90; angle < 270; angle += 60) {
        const coordinate = google.maps.geometry.spherical.computeOffset(
          googlePosition,
          parseFloat(shape.radius),
          angle
        );
        bounds.extend(coordinate);
      }
      this.map.fitBounds(bounds);

      this._attachCircleListeners(circle);
    } else if (shape.type === "point" && shape.position) {
      const marker = new google.maps.Marker({
        position: shape.position,
        map: this.map,
        draggable: Boolean(this.props.allowPointDrag),
        animation: google.maps.Animation.DROP,
      });
      this.markers.push(marker);
      if (this.props.allowPointDrag) {
        this._attachPointListeners(marker);
      }
      bounds.extend(shape.position);
      this.map.fitBounds(bounds);
    }

    // Notifier le parent
    if (this.props.onCoordsChange) {
      this.props.onCoordsChange(shape);
    }
  }

  _drawShapeWithoutClearing(shape) {
    console.log("🟢 _drawShapeWithoutClearing appelé avec :", shape);

    if (!this.map) {
      console.warn("Map pas encore initialisée, on attend un peu...");
      setTimeout(() => this._drawShapeWithoutClearing(shape), 500);
      return;
    }

    const { google } = this.props;
    if (!google || !shape) return;

    const bounds = new google.maps.LatLngBounds();
    let overlay = null;

    if (shape.type === "polygon") {
      overlay = new google.maps.Polygon({
        paths: shape.paths,
        editable: shape.editable !== undefined ? shape.editable : true,
        draggable: false,
        strokeColor: shape.strokeColor || "#FF0000",
        strokeOpacity:
          shape.strokeOpacity !== undefined ? shape.strokeOpacity : 0.8,
        strokeWeight: shape.strokeWeight !== undefined ? shape.strokeWeight : 2,
        fillColor: shape.fillColor || "#FF0000",
        fillOpacity: shape.fillOpacity !== undefined ? shape.fillOpacity : 0.35,
        map: this.map,
      });
      this._attachPolygonListeners(overlay);

      shape.paths.forEach((c) => bounds.extend(c));
    } else if (shape.type === "circle") {
      overlay = new google.maps.Circle({
        strokeColor: "#000000",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#0000FF",
        fillOpacity: 0.35,
        map: this.map,
        center: shape.center,
        radius: parseFloat(shape.radius),
        editable: true,
        draggable: true,
      });
      this._attachCircleListeners(overlay);

      const googlePosition = new google.maps.LatLng(
        shape.center.lat,
        shape.center.lng
      );
      for (let angle = -90; angle < 270; angle += 60) {
        const coordinate = google.maps.geometry.spherical.computeOffset(
          googlePosition,
          parseFloat(shape.radius),
          angle
        );
        bounds.extend(coordinate);
      }

      // Marker facultatif
      const marker = new google.maps.Marker({
        position: shape.center,
        map: this.map,
        animation: google.maps.Animation.DROP,
      });
      this.markers.push(marker);
    } else if (shape.type === "point" && shape.position) {
      const marker = new google.maps.Marker({
        position: shape.position,
        map: this.map,
        animation: google.maps.Animation.DROP,
      });
      this.markers.push(marker);
      bounds.extend(shape.position);
    }

    // Ajouter à la liste des overlays sans effacer les autres
    if (overlay) this.overlays.push(overlay);

    // Ajuster la vue
    this.map.fitBounds(bounds);

    // Notifier le parent
    if (this.props.onCoordsChange) {
      this.props.onCoordsChange(shape);
    }
  }

  // 🔹 Dessiner une forme et retourner l'overlay
  _drawShapeAndReturnOverlay(shape) {
    const { google } = this.props;
    if (!google || !shape) return null;

    if (!this.map) {
      setTimeout(() => this._drawShapeAndReturnOverlay(shape), 300);
      return null;
    }

    const bounds = new google.maps.LatLngBounds();
    let overlay = null;

    if (shape.type === "polygon") {
      overlay = new google.maps.Polygon({
        paths: shape.paths,
        map: this.map,
        editable: false,
        draggable: false,
        strokeColor: "#FF0000",
        fillColor: "#FF0000",
        strokeOpacity: 0.8,
        fillOpacity: 0.35,
        strokeWeight: 2,
      });

      shape.paths.forEach((c) => bounds.extend(c));
    } else if (shape.type === "circle") {
      overlay = new google.maps.Circle({
        center: shape.center,
        radius: parseFloat(shape.radius),
        map: this.map,
        editable: false,
        draggable: false,
        strokeColor: "#000000",
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: "#0000FF",
        fillOpacity: 0.35,
      });

      const googleCenter = new google.maps.LatLng(
        shape.center.lat,
        shape.center.lng
      );

      for (let a = -90; a < 270; a += 60) {
        const p = google.maps.geometry.spherical.computeOffset(
          googleCenter,
          parseFloat(shape.radius),
          a
        );
        bounds.extend(p);
      }
    } else if (shape.type === "point" && shape.position) {
      bounds.extend(shape.position);
    }

    // this.map.fitBounds(bounds);
    return overlay;
  }

  _flattenAois = (aois, parentColor = null, level = 0) => {
    return aois.flatMap((aoi) => {
      const hasExplicitColor = Boolean(aoi?.strokeColor || aoi?.fillColor);
      const color = hasExplicitColor
        ? {
            stroke: aoi.strokeColor || aoi.fillColor || "#2f80ed",
            fill: aoi.fillColor || aoi.strokeColor || "#2f80ed",
          }
        : parentColor || this._randomColor();
  
      const current = {
        ...aoi,
        __level: level,
        __color: color,
        __hasExplicitColor: hasExplicitColor,
      };
  
      const children = Array.isArray(aoi.children)
        ? this._flattenAois(aoi.children, color, level + 1)
        : [];
  
      return [current, ...children];
    });
  };
  

  _randomColor = () => {
    const hue = Math.floor(Math.random() * 360);
    return {
      stroke: `hsl(${hue}, 80%, 25%)`,
      fill: `hsl(${hue}, 80%, 60%)`,
    };
  };
  

  _drawAllAois = (aois) => {
    this.clearMarkers();
    this.overlays.forEach((o) => o.setMap(null));
    this.overlays = [];
    const { google } = this.props;
    const globalBounds = new google.maps.LatLngBounds();
  
    // 🔥 1. Aplatir AOIs + children
    const flatAois = this._flattenAois(aois);
  
    // 🔥 2. Construire les shapes
    const shapes = flatAois.map((aoi) => {
      if (aoi.geometry === "circle") {
        return {
          ...aoi,
          type: "circle",
          center: { lat: aoi.latitude, lng: aoi.longitude },
          radius: aoi.radius,
        };
      } else if (
        String(aoi.geometry || "").toLowerCase() === "polygon" &&
        aoi?.metaData
      ) {
        return {
          ...aoi,
          type: "polygon",
          paths: JSON.parse(aoi.metaData || "[]").map(([lat, lng]) => ({ lat, lng })),
        };
      } else {
        return {
          ...aoi,
          type: "point",
          position: { lat: aoi.latitude, lng: aoi.longitude },
        };
      }
    });

    // 2️⃣ Dessiner et vérifier inclusion
    shapes.forEach((shape, i) => {
      if (shape.type === "polygon" && shape.paths?.length > 0) {
        shape.paths.forEach(p => {
          globalBounds.extend(
            new google.maps.LatLng(p.lat, p.lng)
          );
        });
      }

      if (shape.type === "circle" && shape.center) {
        globalBounds.extend(
          new google.maps.LatLng(shape.center.lat, shape.center.lng)
        );
      }
      if (shape.type === "point" && shape.position) {
        globalBounds.extend(
          new google.maps.LatLng(shape.position.lat, shape.position.lng)
        );
      }
      // Dessiner l'overlay
      const overlay = this._drawShapeAndReturnOverlay(shape);
      if (overlay && shape.__color) {
        overlay.setOptions({
          strokeColor: shape.__color.stroke,
          strokeOpacity: 1,              // 🔥 important
          strokeWeight: shape.__level > 0 ? 3 : 2, // enfants plus marqués
          fillColor: shape.__color.fill,
          fillOpacity: shape.__level > 0 ? 0.35 : 0.2,
        });
        
      }
      
      if (overlay) {
        overlay.aoiId = shape.id;
        this.overlays.push(overlay);

        // Vérifier si inclus dans une autre
        let isInsideAnother = false;
        for (let j = 0; j < shapes.length; j++) {
          if (i === j) continue;
          const other = shapes[j];

          if (shape.type === "circle" && other.type === "circle") {
            const dist = google.maps.geometry.spherical.computeDistanceBetween(
              new google.maps.LatLng(shape.center.lat, shape.center.lng),
              new google.maps.LatLng(other.center.lat, other.center.lng)
            );
            if (dist + shape.radius <= other.radius) {
              isInsideAnother = true;
              break;
            }
          } else if (shape.type === "polygon" && other.type === "polygon") {
            // tous les points de shape doivent être dans other
            isInsideAnother = shape.paths.every((p) =>
              google.maps.geometry.poly.containsLocation(
                new google.maps.LatLng(p.lat, p.lng),
                new google.maps.Polygon({ paths: other.paths })
              )
            );
            if (isInsideAnother) break;
          }
        }

        // 3️⃣ Appliquer le style
        if (isInsideAnother && !shape.__hasExplicitColor) {
          if (shape.type === "circle") {
            overlay.setOptions({
              strokeColor: "#FFD700",
              fillColor: "#FFFF00",
              fillOpacity: 0.4,
            });
          } else if (shape.type === "polygon") {
            overlay.setOptions({
              strokeColor: "#FFD700",
              fillColor: "#FFFF00",
              fillOpacity: 0.4,
            });
          }
        }
      }

      // 4️⃣ Ajouter le marker et la bulle
      const markerPosition =
        shape.position || shape.center || { lat: shape.latitude, lng: shape.longitude };
      if (markerPosition.lat && markerPosition.lng) {
        const marker = new google.maps.Marker({
          position: markerPosition,
          map: this.map,
          animation: google.maps.Animation.DROP,
        });
        this.markers.push(marker);

        let bubble = null;
        let isHoveringOverlay = false;

        const showBubble = () => {
          if (!bubble) {
            bubble = new CustomInfoBubble(
              marker.getPosition(),
              shape.name || "AOI",
              this.map
            );
          }
        };
        const hideBubble = () => {
          if (bubble) {
            bubble.setMap(null);
            bubble = null;
          }
        };

        marker.addListener("mouseover", showBubble);
        marker.addListener("mouseout", () => {
          setTimeout(() => {
            if (!isHoveringOverlay) hideBubble();
          }, 100);
        });
        marker.addListener("click", () => {
          if (this.props.onAoiClick) {
            this.props.onAoiClick(shape);
          }
        });

        if (overlay) {
          overlay.addListener("mouseover", () => {
            isHoveringOverlay = true;
            showBubble();
          });
          overlay.addListener("mouseout", () => {
            isHoveringOverlay = false;
            hideBubble();
          });
          overlay.addListener("click", () => {
            if (this.props.onAoiClick) {
              this.props.onAoiClick(shape);
            }
          });
        }
      }
    });
    if (!globalBounds.isEmpty()) {
      this.map.fitBounds(globalBounds);
}
  };

  // ===============================
  // 🔸 Créer les markers depuis les props
  // ===============================
  _createMarkersFromProps = () => {
    const { google, markers } = this.props;

    if (!google || !this.map || !markers || markers.length === 0) {
      console.log("❌ Impossible de créer les markers:", {
        google: !!google,
        map: !!this.map,
        markers: markers?.length || 0,
      });
      return;
    }

    console.log(`🎯 Création de ${markers.length} markers depuis props`);

    // Clear existing markers first
    this.clearMarkers();

    // Clear existing info windows
    this.infoWindows.forEach((infoWindow) => infoWindow.close());
    this.infoWindows = [];

    const bounds = new google.maps.LatLngBounds();

    markers.forEach((markerConfig, index) => {
      if (!markerConfig.position) {
        console.warn(`⚠️ Marker ${index} n'a pas de position:`, markerConfig);
        return;
      }

      const position = {
        lat: parseFloat(markerConfig.position.lat),
        lng: parseFloat(markerConfig.position.lng),
      };

      console.log(`📍 Création marker ${index}:`, position);

      const marker = new google.maps.Marker({
        position: position,
        map: this.map,
        title: markerConfig.title || `Marker ${index + 1}`,
        icon: markerConfig.icon || null,
        draggable: markerConfig.draggable || false,
        animation: google.maps.Animation.DROP,
      });

      // Étendre les bounds
      bounds.extend(marker.getPosition());

      // Add click listener if provided
      if (markerConfig.onClick) {
        marker.addListener("click", () => {
          console.log(`Marker ${index} cliqué`);
          markerConfig.onClick(markerConfig, marker);
        });
      }

      // Add info window if provided
      if (markerConfig.infoWindow) {
        const infoWindow = new google.maps.InfoWindow({
          content: markerConfig.infoWindow,
        });

        marker.addListener("click", () => {
          // Close all other info windows
          this.infoWindows.forEach((iw) => iw.close());
          infoWindow.open(this.map, marker);
        });

        this.infoWindows.push(infoWindow);
      }

      // Add hover effect
      if (markerConfig.onHover) {
        marker.addListener("mouseover", () =>
          markerConfig.onHover(markerConfig, marker)
        );
        marker.addListener("mouseout", () =>
          markerConfig.onHoverEnd &&
          markerConfig.onHoverEnd(markerConfig, marker)
        );
      }

      this.markers.push(marker);
    });

    // Ajuster la vue pour montrer tous les markers
   if (this.markers.length > 0 && this.props.area && this.hasAdjustedMarkersView !== true) {

      if (this.markers.length === 1) {
        this.map.setCenter(this.markers[0].getPosition());
        this.map.setZoom(15);
      } else {
        this.map.fitBounds(bounds);
      }

    }

    // Activer après premier passage
    this.hasAdjustedMarkersView = true;
  };

  // ===============================
  // 🔸 Appliquer le thème clair/sombre
  // ===============================
  _applyTheme() {
    if (!this.map) return;
    const { theme } = this.props;
    if (theme.mode === "dark") {
      this.map.setOptions({ styles: darkMapStyles });
    } else {
      this.map.setOptions({ styles: [] });
    }
  }

  _setMapTypeFromExternalControl = (event) => {
    const requestedScope = event?.detail?.scope;
    const requestedMapType = event?.detail?.mapTypeId;
    const componentScope = this.props.mapControlScope;

    if (!this.map || !requestedMapType) return;
    if (componentScope && requestedScope && componentScope !== requestedScope) return;

    this.map.setMapTypeId(requestedMapType);
  };

  // ===============================
  // 🔸 Initialiser le DrawingManager
  // ===============================
  _initDrawingManager() {
    const { google } = this.props;
   
    this.drawingManager = new google.maps.drawing.DrawingManager({
      drawingMode: null,
      drawingControl: false,
      drawingControlOptions: {
        position: google.maps.ControlPosition.TOP_CENTER,
        drawingModes: [
          google.maps.drawing.OverlayType.POLYGON,
          google.maps.drawing.OverlayType.CIRCLE,
        ],
      },
    });

    this.drawingManager.setMap(this.map);

    // Lorsqu'un dessin est terminé
    google.maps.event.addListener(
      this.drawingManager,
      "overlaycomplete",
      (event) => {
        if (this.currentOverlay) {
          this.currentOverlay.setMap(null);
        }

        this.currentOverlay = event.overlay;

        if (event.type === "polygon") {
          this.currentOverlay.setEditable(true);
          this._attachPolygonListeners(event.overlay);
        } else if (event.type === "circle") {
          this._attachCircleListeners(event.overlay);
        }

        this._handleOverlay(event);
      }
    );
  }

  // ===============================
  // 🔸 Écouteurs sur polygone modifié
  // ===============================
  _attachPolygonListeners(polygon) {
    const { google } = this.props;
    const updatePath = () => {
      const updated = polygon
        .getPath()
        .getArray()
        .map((p) => ({ lat: p.lat(), lng: p.lng() }));

      this.setState({ coords: updated });

      if (this.props.onCoordsChange) {
        this.props.onCoordsChange(updated);
      }

      console.log("🔁 Polygone modifié :", updated);
    };

    google.maps.event.addListener(polygon.getPath(), "set_at", updatePath);
    google.maps.event.addListener(polygon.getPath(), "insert_at", updatePath);
  }

  // ===============================
  // 🔸 Écouteurs sur cercle modifié
  // ===============================
  _attachCircleListeners(circle) {
    const { google } = this.props;

    const logCircle = () => {
      const coords = {
        center: circle.getCenter().toJSON(),
        radius: circle.getRadius(),
      };

      this.setState({ coords });

      if (this.props.onCoordsChange) {
        this.props.onCoordsChange(coords);
      }

      console.log("🔁 Cercle modifié :", coords);
    };

    google.maps.event.addListener(circle, "center_changed", logCircle);
    google.maps.event.addListener(circle, "radius_changed", logCircle);
  }

  _attachPointListeners(marker) {
    const emitPoint = () => {
      const position = marker.getPosition()?.toJSON();
      if (!position) return;

      const point = {
        type: "point",
        position,
      };

      this.setState({ coords: point });

      if (this.props.onCoordsChange) {
        this.props.onCoordsChange(point);
      }
    };

    marker.addListener("dragend", emitPoint);
  }

  // ===============================
  // 🔸 Gestion selon type d'élément
  // ===============================
  _handleOverlay(event) {
    const type = event.type;

    if (type === "polygon") {
      const coords = event.overlay
        .getPath()
        .getArray()
        .map((p) => ({ lat: p.lat(), lng: p.lng() }));

      this.setState({ coords });
      if (this.props.onCoordsChange) {
        this.props.onCoordsChange(coords);
      }

      console.log("🟦 Polygone :", coords);
    } else if (type === "circle") {
      const coords = {
        center: event.overlay.getCenter().toJSON(),
        radius: event.overlay.getRadius(),
      };

      this.setState({ coords });
      if (this.props.onCoordsChange) {
        this.props.onCoordsChange(coords);
      }

      console.log("🟢 Cercle :", coords);
    }
  }

  // ===============================
  // 🔸 Activer/désactiver le mode dessin
  // ===============================
  toggleDrawing = () => {
    if (!this.drawingManager) return;

    const { google } = this.props;
    const enabled = !this.props.drawingEnabled;

    this.props.enableDrawing(enabled);

    this.drawingManager.setOptions({
      drawingControl: enabled,
      drawingMode: enabled ? google.maps.drawing.OverlayType.POLYGON : null,
    });
  };

  _syncDrawingModeFromProps = (prevProps) => {
    if (!this.drawingManager || !this.props.google) return;

    const geometryChanged =
      prevProps?.geometrySelection !== this.props.geometrySelection;
    const drawingChanged =
      prevProps?.drawingEnabled !== this.props.drawingEnabled;

    if (!geometryChanged && !drawingChanged && prevProps) return;

    const { google } = this.props;
    const isPolygonMode = this.props.geometrySelection === "polygon-perso";

    if (isPolygonMode) {
      this.drawingManager.setOptions({
        drawingControl: true,
        drawingMode: this.props.drawingEnabled
          ? google.maps.drawing.OverlayType.POLYGON
          : null,
      });

      if (!this.props.drawingEnabled && !prevProps) {
        this.props.enableDrawing(true);
      }
      return;
    }

    this.drawingManager.setOptions({
      drawingControl: false,
      drawingMode: null,
    });
  };

  // ===============================
  // 🔸 Lifecycle React
  // ===============================
  componentDidMount() {
    console.log("🚀 Map componentDidMount");
    this._setMap();
    this.externalMapTypeListener = this._setMapTypeFromExternalControl;
    window.addEventListener("lewoo:set-map-type", this.externalMapTypeListener);

    // Exposer la fonction clearShape au parent
    if (this.props.setClearShape) {
      this.props.setClearShape(this.clearShape);
    }

    if (this.props.setClearMarkers) {
      this.props.setClearMarkers(this.clearMarkers);
    }
    if (this.props.setDrawShape) {
      console.log("✅ setDrawShape reçu dans Map");
      this.props.setDrawShape(this._drawInitialShape.bind(this));
    }
    if (this.props.registerDrawFunction) {
      console.log("✅ Fonction de dessin enregistrée !");
      this.props.registerDrawFunction(
        this._drawShapeWithoutClearing.bind(this)
      );
    }

    if (this.props.setDrawAllAois) {
      console.log("✅ setDrawAllAois reçu dans Map");
      this.props.setDrawAllAois(this._drawAllAois.bind(this));
    }

    // Activer le dessin automatiquement si geometrySelection = polygon-perso
    this._syncDrawingModeFromProps();

    if (this.props.setShowSidebarRef) {
      this.props.setShowSidebarRef({
        show: this.showSidebar,
        hide: this.hideSidebar,
      });
    }
  }

  componentWillUnmount() {
    if (this.externalMapTypeListener) {
      window.removeEventListener("lewoo:set-map-type", this.externalMapTypeListener);
    }
  }

  componentDidUpdate(prevProps) {
    console.log("🔄 Map componentDidUpdate");

    this._syncDrawingModeFromProps(prevProps);

    if (prevProps.theme !== this.props.theme) {
      this._applyTheme();
    }

    // 🔥 NOUVEAU : tracer toutes les AOIs quand le props change
  if (
    JSON.stringify(prevProps.aois) !== JSON.stringify(this.props.aois)
  ) {
    console.log("🗺️ Mise à jour des AOIs détectée");

    if (this.state.mapInitialized && this.props.aois?.length > 0) {
      this._drawAllAois(this.props.aois);
    }
  }

   // 🔥 AOIs changent
   if (prevProps.aois !== this.props.aois) {
    console.log("🔄 AOIs props modifiées");

    if (!this.map) {
      console.warn("⏳ Map pas encore prête, on attend...");
      return;
    }

    if (this.props.aois && this.props.aois.length > 0) {
      this._drawAllAois(this.props.aois);
    } else {
      // si tableau vide → nettoyer
      this.overlays.forEach(o => o.setMap(null));
      this.overlays = [];
      this.clearMarkers();
    }
  }

    // Re-créer les markers si la prop markers change
    if (
      JSON.stringify(prevProps.markers) !== JSON.stringify(this.props.markers)
    ) {
      console.log("🔄 Mise à jour des markers détectée");
      if (this.state.mapInitialized) {
        this._createMarkersFromProps();
      } else {
        // Attendre que la carte soit initialisée
        console.log("⏳ Attente de l'initialisation de la carte...");
        const checkMap = setInterval(() => {
          if (this.state.mapInitialized) {
            clearInterval(checkMap);
            this._createMarkersFromProps();
          }
        }, 100);
      }
    }
  }

  // 🔹 Exemple d'attachCircleListeners
  _attachCircleListeners(circle) {
    const updateCircle = () => {
      const shape = {
        type: "circle",
        center: circle.getCenter().toJSON(),
        radius: circle.getRadius(),
      };
      this.setState({ coords: shape });
      if (this.props.onCoordsChange) this.props.onCoordsChange(shape);
      console.log("🔁 Cercle modifié :", shape);
    };

    circle.addListener("center_changed", updateCircle);
    circle.addListener("radius_changed", updateCircle);
  }

  // 🔹 Fonction pour effacer la forme
  clearShape = () => {
    if (this.currentOverlay) {
      this.currentOverlay.setMap(null);
      this.currentOverlay = null;
      this.setState({ coords: [] });

      if (this.props.onCoordsChange) {
        this.props.onCoordsChange(null);
      }
      this.clearMarkers();

      console.log("🗑️ Forme effacée !");
    }
  };

  clearMarkers = () => {
    if (this.markers && this.markers.length > 0) {
      this.markers.forEach((marker) => marker.setMap(null));
      this.markers = [];
      console.log("🗑️ Tous les markers ont été supprimés !");
    }

    // Fermer toutes les info windows
    if (this.infoWindows && this.infoWindows.length > 0) {
      this.infoWindows.forEach((infoWindow) => infoWindow.close());
      this.infoWindows = [];
    }
  };

  // Dans la classe Map
  showSidebar = () => {
    this.setState({ showOverlayBar: true });
  };

  hideSidebar = () => {
    this.setState({ showOverlayBar: false });
  };

  // ================================
  // 🔹 Méthodes de contrôle overlay
  // ================================
  toggleOverlayBar = () => {
    this.setState({ showOverlayBar: !this.state.showOverlayBar });
  };

  showOverlay = () => {
    this.setState({ showOverlayBar: true });
  };

  hideOverlay = () => {
    this.setState({ showOverlayBar: false });
  };

  render() {
    return (
      <div
      className={`google-map-container ${this.props.containerClassName || ""}`}
       
      >
        {/* {this.props.geometrySelection === "polygon-perso" && (
          <button
            className="draw-btn"
            onClick={this.toggleDrawing}
            style={{
              position: "absolute",
              top: 14,
              left: 14,
              zIndex: 999,
              background: this.props.drawingEnabled ? "#dc2626" : "#059669",
              color: "white",
              border: "none",
              padding: "12px 16px",
              borderRadius: "10px",
              boxShadow: "0 10px 25px rgba(15, 23, 42, 0.18)",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {this.props.drawingEnabled
              ? "Désactiver le dessin ------------------------------------------------------------"
              : "Activer le dessin"}
          </button>
        )} */}

        {/* ======================================================
           🔹 BARRE OVERLAY — Apparaît seulement si showOverlayBar
        ========================================================= */}
        {this.state.showOverlayBar && this.props.sidebarIcons.length > 0 && (
          <SidebarIcons
            icons={this.props.sidebarIcons}
            onClose={
              this.props.onCloseSidebar ||
              (() => this.setState({ showOverlayBar: false }))
            }
          />
        )}

        <div
          ref={this.mapRef}
          className="google-map-canvas"
        />
        {this.props.children}
      </div>
    );
  }
}

// ===============================
// 🔹 PropTypes
// ===============================
Map.propTypes = {
  zoom: PropTypes.number,
  center: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  google: PropTypes.object,
  setMap: PropTypes.func,
  theme: PropTypes.object,
  onCoordsChange: PropTypes.func,
  initialCoords: PropTypes.array,
  sidebarIcons: PropTypes.array,
  onCloseSidebar: PropTypes.func,
  markers: PropTypes.arrayOf(
    PropTypes.shape({
      position: PropTypes.shape({
        lat: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
          .isRequired,
        lng: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
          .isRequired,
      }).isRequired,
      title: PropTypes.string,
      icon: PropTypes.string,
      infoWindow: PropTypes.string,
      draggable: PropTypes.bool,
      onClick: PropTypes.func,
      onHover: PropTypes.func,
      onHoverEnd: PropTypes.func,
    })
  ),
  area: PropTypes.shape({
    latitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    longitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  drawingEnabled: PropTypes.bool,
  enableDrawing: PropTypes.func,
  geometrySelection: PropTypes.string,
  setDrawAllAois: PropTypes.func,
  setClearShape: PropTypes.func,
  setClearMarkers: PropTypes.func,
  setDrawShape: PropTypes.func,
  registerDrawFunction: PropTypes.func,
  setShowSidebarRef: PropTypes.func,
  initialShape: PropTypes.object,
  containerClassName: PropTypes.string,
  mapControlScope: PropTypes.string,
  aois: PropTypes.array,
  onAoiClick: PropTypes.func,
  allowPointDrag: PropTypes.bool,
};

Map.defaultProps = {
  zoom: 4,
  google: null,
  setMap: () => {},
  onCoordsChange: () => {},
  initialCoords: [],
  sidebarIcons: [],
  onCloseSidebar: null,
  markers: [],
  area: null,
  drawingEnabled: false,
  enableDrawing: () => {},
  geometrySelection: null,
  containerClassName: "",
  allowPointDrag: false,
  aois: [],
  onAoiClick: null,

};

// ===============================
// 🔹 Composant avec requête GraphQL
// ===============================
const MyMapWithClient = (props) => {
  const userId = parseInt(getCookie("userId"));

  const { data, loading, error } = useQuery(GET_CLIENT_DETAIL, {
    variables: { loginId: userId },
    skip: !userId,
  });

  if (loading) return <div>Chargement...</div>;
  if (error) return <div>Erreur: {error.message}</div>;

  const lat = data?.clientDetail?.lat ?? 7.36;
  const lng = data?.clientDetail?.long ?? 12.35;

  return <Map center={{ lat, lng }} {...props} />;
};

export default withTheme(MyMapWithClient);
